/**
 * useV1Data — hooks read-only pour les collections Firestore V1.
 * Lit les clés `appdata/{key}` au format V1 : { value: [...], updatedAt: ... }
 * Aucune écriture. Aucune migration. Source unique : Firestore V1.
 */
import { useState, useEffect, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

function useFirestoreRead(key) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const unsubRef = useRef(null);

  useEffect(() => {
    if (!key || !db) {
      setLoading(false);
      return;
    }
    // Attach listener
    try {
      unsubRef.current = onSnapshot(doc(db, "appdata", key), (snap) => {
        if (snap.exists()) {
          const raw = snap.data().value;
          setData(raw !== undefined ? raw : null);
        } else {
          setData(null);
        }
        setLoading(false);
      }, () => {
        // Permission error or offline — fail gracefully
        setData(null);
        setLoading(false);
      });
    } catch {
      setData(null);
      setLoading(false);
    }
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [key]);

  return { data, loading };
}

/* ── Exports nommés par collection ── */

export function useUsers() {
  const { data, loading } = useFirestoreRead("ep:users");
  return { users: data || [], loading };
}

export function useTasks() {
  const { data, loading } = useFirestoreRead("ep:tasks");
  return { tasks: data || [], loading };
}

export function useRequests() {
  const { data, loading } = useFirestoreRead("ep:requests");
  return { requests: data || [], loading };
}

export function usePublications() {
  const { data, loading } = useFirestoreRead("ep:publications");
  return { publications: data || [], loading };
}

export function useObjectives() {
  const { data, loading } = useFirestoreRead("ep:objectives");
  return { objectives: data || [], loading };
}

export function useProjects() {
  const { data, loading } = useFirestoreRead("ep:projects");
  return { projects: data || [], loading };
}

export function useCampagnes() {
  const { data, loading } = useFirestoreRead("ep:campagnes");
  return { campagnes: data || [], loading };
}

export function useRessources() {
  const { data, loading } = useFirestoreRead("ep:ressources");
  return { ressources: data || [], loading };
}

export function useCalendarEvents() {
  const { data, loading } = useFirestoreRead("ep:events");
  return { events: data || [], loading };
}

export function useReporting() {
  const { data, loading } = useFirestoreRead("ep:reporting");
  return { reporting: data || [], loading };
}

export function useClubs() {
  const { data, loading } = useFirestoreRead("ep:clubs");
  return { clubs: data || [], loading };
}

export function useMeetings() {
  const { data, loading } = useFirestoreRead("ep:meetings");
  return { meetings: data || [], loading };
}

export function useMetricool() {
  const { data, loading } = useFirestoreRead("ep:metricool-approvals");
  return { metricool: data || null, loading };
}

export function useCommEvents() {
  const { data, loading } = useFirestoreRead("ep:commevents");
  return { commEvents: Array.isArray(data) ? data : [], loading };
}

export function useEventEditors() {
  const { data, loading } = useFirestoreRead("ep:eventeditors");
  return { eventEditors: Array.isArray(data) ? data : [], loading };
}

/* ── KPI & Reporting ── */

/**
 * État de la dernière synchronisation Metricool (source, mois, fraîcheur, erreurs).
 */
export function useSyncStatus() {
  const { data, loading } = useFirestoreRead("ep:metricool-sync-status");
  return { syncStatus: data && typeof data === "object" ? data : null, loading };
}

/**
 * Chiffres saisis manuellement (campagnes / opérations locales / web).
 * Forme : { [entryId]: { id, scope, refId, clubId, month, values{}, source:"manuel", ... } }
 */
export function useKpiManual() {
  const { data, loading } = useFirestoreRead("ep:kpi-manual");
  return { kpiManual: data && typeof data === "object" && !Array.isArray(data) ? data : {}, loading };
}

/**
 * Historique des bilans générés (consultables).
 */
export function useKpiReports() {
  const { data, loading } = useFirestoreRead("ep:kpi-reports");
  return { kpiReports: Array.isArray(data) ? data : [], loading };
}

/**
 * Lecture multi-clés des documents de détail Metricool.
 * Le shim lit une clé par abonnement ; ce hook gère un nombre VARIABLE de clés
 * (mois × clubs sélectionnés) dans un seul effet, avec teardown au changement de liste.
 *
 * @param {string[]} keys  clés complètes "ep:reporting-details:<mois>:<clubId>"
 * @returns {{ details: Object<string, any>, loading: boolean }}
 */
export function useReportingDetails(keys) {
  const keyList = Array.isArray(keys) ? keys.filter(Boolean) : [];
  const joined = keyList.join("|");
  const [byKey, setByKey] = useState({});
  const [loading, setLoading] = useState(keyList.length > 0);

  useEffect(() => {
    const list = joined ? joined.split("|") : [];
    if (!db || list.length === 0) {
      setByKey({});
      setLoading(false);
      return;
    }
    setLoading(true);
    let remaining = list.length;
    const settle = () => {
      if (remaining > 0) {
        remaining -= 1;
        if (remaining === 0) setLoading(false);
      }
    };
    const unsubs = list.map((key) => {
      try {
        return onSnapshot(
          doc(db, "appdata", key),
          (snap) => {
            const val = snap.exists() ? snap.data().value : null;
            setByKey((prev) => ({ ...prev, [key]: val !== undefined ? val : null }));
            settle();
          },
          () => {
            setByKey((prev) => ({ ...prev, [key]: null }));
            settle();
          }
        );
      } catch {
        settle();
        return null;
      }
    });
    return () => unsubs.forEach((u) => u && u());
  }, [joined]);

  return { details: byKey, loading };
}

/* ── Helpers ── */

/**
 * Retourne les tâches à traiter aujourd'hui pour un appId donné.
 * Critères : non terminée ET (échéance ≤ aujourd'hui OU urgence haute).
 */
export function filterTodayTasks(tasks, appId) {
  if (!Array.isArray(tasks)) return [];
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const me = String(appId);
  // Appartenance tolérante : `assignedTo` est, dans les données réelles, un TABLEAU
  // d'ids de types mixtes (nombre, flottant, grande chaîne). Absent ou vide = non
  // assigné (visible par tous). Comparaison toujours stringifiée (ids mixtes).
  const isMine = (assignedTo) => {
    if (assignedTo == null) return true;
    if (Array.isArray(assignedTo)) {
      return assignedTo.length === 0 || assignedTo.some((x) => String(x) === me);
    }
    return String(assignedTo) === me;
  };
  // Urgence/priorité : données réelles capitalisées ("Haute", "Urgent"…) → normaliser.
  const isHigh = (v) => {
    const s = String(v || "").toLowerCase();
    return s === "haute" || s === "high" || s === "urgent";
  };
  return tasks.filter((t) => {
    if (!t) return false;
    // Statut réel : "À faire" | "En cours" | "Terminé" | "Terminée". Normaliser pour
    // couvrir les variantes de casse/accent ("termin…" couvre "terminé"/"terminée").
    const status = String(t.status || "").toLowerCase();
    const done = t.done === true || status.startsWith("termin") || status === "done" || status === "completed";
    if (done) return false;
    if (!isMine(t.assignedTo)) return false;
    const isUrgent = isHigh(t.urgency) || isHigh(t.priority);
    const hasDue = t.dueDate || t.deadline || t.date;
    if (hasDue) {
      const d = new Date(hasDue);
      if (!isNaN(d.getTime()) && d <= today) return true;
    }
    if (isUrgent) return true;
    return false;
  });
}

/**
 * Retourne les demandes clubs ouvertes (non clôturées, non refusées).
 */
export function filterOpenRequests(requests) {
  if (!Array.isArray(requests)) return [];
  const closed = ["clôturée", "closed", "refusée", "refused", "rejected"];
  return requests.filter((r) => r && !closed.includes((r.status || "").toLowerCase()));
}

/**
 * Retourne les publications en attente de validation.
 */
export function filterPendingValidations(publications) {
  if (!Array.isArray(publications)) return [];
  return publications.filter((p) => {
    if (!p) return false;
    const s = (p.status || p.statut || "").toLowerCase();
    return s === "review" || s === "à valider" || s === "pending" || s === "review_pending";
  });
}

/**
 * Retourne les objectifs actifs.
 */
export function filterActiveObjectives(objectives) {
  if (!Array.isArray(objectives)) return [];
  return objectives.filter((o) => {
    if (!o) return false;
    const s = (o.status || o.statut || "").toLowerCase();
    return s === "actif" || s === "active" || s === "en cours" || (!s && !o.archived);
  });
}
